// AnnouncementEmailDialog.js
//
// Generic "send a Mandrill template to a filtered audience" admin tool.
// Templates are fetched live from Mandrill, so pushing a new template there
// is enough to use it here - no backend config/secret/deploy is needed per
// template (see mailchimp_service.py list_transactional_templates /
// send_bulk_transactional_email).
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  Alert,
  Divider
} from '@mui/material';
import { Campaign, Send, MarkEmailRead } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../../auth/AuthContext';
import mailchimpService from '../../../services/mailChimpService';

const AUDIENCE_OPTIONS = [
  {
    value: 'active_inactive',
    label: 'Active + Inactive users',
    description: 'Anyone with an account who has logged in at least once. Excludes Pending Setup and Suspended users.'
  }
];

const AnnouncementEmailDialog = ({ open, onClose }) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [subject, setSubject] = useState('');

  const [audience, setAudience] = useState('active_inactive');
  const [audienceCount, setAudienceCount] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(false);

  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [testSending, setTestSending] = useState(false);

  // null when closed, otherwise 'all' (full audience) or 'retry' (failed only)
  const [confirmMode, setConfirmMode] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const resetState = useCallback(() => {
    setSelectedTemplate('');
    setSubject('');
    setAudience('active_inactive');
    setAudienceCount(null);
    setSendResult(null);
    setConfirmMode(null);
  }, []);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const response = await mailchimpService.listAnnouncementTemplates(enqueueSnackbar);
      setTemplates(response?.templates || []);
    } catch (err) {
      // Error already surfaced via enqueueSnackbar in the service
    } finally {
      setTemplatesLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadAudienceCount = useCallback(async (audienceValue) => {
    setAudienceLoading(true);
    try {
      const response = await mailchimpService.getAnnouncementAudience(audienceValue, enqueueSnackbar);
      setAudienceCount(response?.count ?? null);
    } catch (err) {
      setAudienceCount(null);
    } finally {
      setAudienceLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (open) {
      resetState();
      loadTemplates();
      loadAudienceCount('active_inactive');
      setTestEmail(user?.email || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleTemplateChange = (templateName) => {
    setSelectedTemplate(templateName);
    const template = templates.find((t) => t.name === templateName);
    if (template?.subject && !subject) {
      setSubject(template.subject);
    }
  };

  const handleAudienceChange = (value) => {
    setAudience(value);
    loadAudienceCount(value);
  };

  const handleSendTest = async () => {
    if (!selectedTemplate || !subject || !testEmail) return;
    setTestSending(true);
    try {
      await mailchimpService.sendAnnouncement(
        { templateName: selectedTemplate, subject, testEmail },
        enqueueSnackbar
      );
      enqueueSnackbar(`Test email sent to ${testEmail}`, { variant: 'success' });
    } catch (err) {
      // Error already surfaced via enqueueSnackbar in the service
    } finally {
      setTestSending(false);
    }
  };

  const failedRecipients = sendResult?.failed_recipients || [];
  const retryableEmails = failedRecipients.filter((f) => f.retryable).map((f) => f.email);

  const handleSend = async (retryEmails = null) => {
    setConfirmMode(null);
    setSending(true);
    try {
      const response = await mailchimpService.sendAnnouncement(
        { templateName: selectedTemplate, subject, audience, retryEmails },
        enqueueSnackbar
      );
      setSendResult((prev) => {
        if (!retryEmails || !prev) return response;
        const stillFailed = [
          ...(prev.failed_recipients || []).filter((f) => !f.retryable),
          ...(response?.failed_recipients || [])
        ];
        return {
          ...response,
          sent_count: (prev.sent_count || 0) + (response?.sent_count || 0),
          failed_count: stillFailed.length,
          failed_recipients: stillFailed
        };
      });
      if (response?.failed_count > 0) {
        enqueueSnackbar(
          `Sent to ${response.sent_count}, but ${response.failed_count} failed`,
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar(`Announcement sent to ${response?.sent_count ?? 0} recipients`, {
          variant: 'success'
        });
      }
    } catch (err) {
      // Error already surfaced via enqueueSnackbar in the service
    } finally {
      setSending(false);
    }
  };

  // Once a full send has gone out, only the failed-only retry is offered, so a
  // second click can't email the whole audience again.
  const canSend = Boolean(selectedTemplate) && Boolean(subject) && !sending && !sendResult;

  return (
    <>
      <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Campaign color="primary" />
          Send Announcement Email
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Send any template already pushed to Mandrill to a filtered set of users -
            no code deploy required per template.
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="announcement-template-label">Mandrill Template</InputLabel>
            <Select
              labelId="announcement-template-label"
              label="Mandrill Template"
              value={selectedTemplate}
              onChange={(e) => handleTemplateChange(e.target.value)}
              disabled={templatesLoading || sending}
            >
              {templatesLoading && (
                <MenuItem value="" disabled>
                  Loading templates...
                </MenuItem>
              )}
              {!templatesLoading && templates.length === 0 && (
                <MenuItem value="" disabled>
                  No templates found in Mandrill
                </MenuItem>
              )}
              {templates.map((template) => (
                <MenuItem key={template.name} value={template.name}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            label="Email Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={sending}
            sx={{ mb: 3 }}
          />

          <Divider sx={{ mb: 2 }} />

          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel id="announcement-audience-label">Audience</InputLabel>
            <Select
              labelId="announcement-audience-label"
              label="Audience"
              value={audience}
              onChange={(e) => handleAudienceChange(e.target.value)}
              disabled={sending}
            >
              {AUDIENCE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {AUDIENCE_OPTIONS.find((o) => o.value === audience)?.description}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            {audienceLoading ? (
              <CircularProgress size={16} />
            ) : (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={
                  audienceCount === null
                    ? 'Recipient count unavailable'
                    : `${audienceCount} recipient${audienceCount === 1 ? '' : 's'}`
                }
              />
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Send a test email first
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Test recipient"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              disabled={testSending}
            />
            <Button
              variant="outlined"
              startIcon={testSending ? <CircularProgress size={16} /> : <MarkEmailRead />}
              onClick={handleSendTest}
              disabled={!selectedTemplate || !subject || !testEmail || testSending}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Send Test
            </Button>
          </Box>

          {sendResult && (
            <Alert
              severity={sendResult.failed_count > 0 ? 'warning' : 'success'}
              sx={{ mt: 2 }}
            >
              Sent to {sendResult.sent_count ?? 0} recipient(s).
              {sendResult.failed_count > 0 && ` ${sendResult.failed_count} failed.`}
              {failedRecipients.length > retryableEmails.length && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Addresses Mandrill rejected (bounced, unsubscribed, spam complaint) can't be
                  retried; they would be rejected again.
                </Typography>
              )}
            </Alert>
          )}

          {failedRecipients.length > 0 && (
            <Box
              sx={{
                mt: 1,
                maxHeight: 160,
                overflowY: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1
              }}
            >
              {failedRecipients.map((f) => (
                <Typography key={f.email} variant="caption" display="block">
                  {f.email}: {f.error}
                  {f.retryable ? ' (retryable)' : ''}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={sending}>
            Close
          </Button>
          {retryableEmails.length > 0 ? (
            <Button
              variant="contained"
              color="warning"
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
              disabled={sending}
              onClick={() => setConfirmMode('retry')}
            >
              Retry {retryableEmails.length} Failed
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
              disabled={!canSend}
              onClick={() => setConfirmMode('all')}
            >
              Send to {audienceCount ?? '...'} Recipients
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={confirmMode !== null} onClose={() => setConfirmMode(null)}>
        <DialogTitle>{confirmMode === 'retry' ? 'Confirm Retry' : 'Confirm Send'}</DialogTitle>
        <DialogContent>
          <Typography>
            Send "{subject}" (template: {selectedTemplate}) to{' '}
            <strong>
              {confirmMode === 'retry'
                ? `the ${retryableEmails.length} recipient(s) that failed`
                : `${audienceCount ?? 'an unknown number of'} recipients`}
            </strong>
            ? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmMode(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSend(confirmMode === 'retry' ? retryableEmails : null)}
          >
            Yes, Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AnnouncementEmailDialog;
